/**
 * @file order-chain-owner.ts
 * @description Реестр «чей ход» — стикеры, цвета и иконки владельцев
 * (phase19.24-order-card-chain-design.md §14).
 *
 * Принцип фазы: цвет в карточке заказа отвечает на ОДИН вопрос — чей сейчас
 * ход. Статусы документов остаются в закрытом реестре `DocumentStatusBadge`,
 * иначе карточка превращается в светофор. Поэтому владелец — запись в этом
 * реестре, а не ветка `if` в пяти компонентах: новый владелец (мастер
 * маркетплейса на этапе 2) добавляется строкой.
 *
 * Регистр подписей обычный («Ваш ход», не «ВАШ ХОД»): капс в админке остался
 * только у `.label-editorial` (UI-контракт §3.3 п.1), а стикер — бейдж.
 * Цвет никогда не единственный признак: у каждого стикера есть текст и иконка.
 */

import {
  AlarmClock,
  Package,
  TriangleAlert,
  Truck,
  User,
  UserRound,
  Zap,
  type LucideIcon,
} from "lucide-react";

import type { OrderChainOwner, OrderChainProblem } from "./order-chain";

/** Описание стикера: что написано, чем нарисовано, какими классами. */
export interface OwnerChipMeta {
  /** Подпись в карточке и полосе «Сейчас»: «Ваш ход», «Склад». */
  label: string;
  /** Короткая подпись под точкой степпера: «вы», «склад», «ТК». */
  short: string;
  icon: LucideIcon;
  /** Классы бейджа (фон + текст) — только токены DS, без hex. */
  chip: string;
  /**
   * Контурный вариант бейджа. Нужен правилу «не больше двух стикеров» (§14):
   * рядом с красным стикером «Ваш ход» не дублирует красный, а показывается
   * контуром — иначе полоса читалась как три тревоги подряд.
   */
  chipOutline: string;
  /** Точка степпера и рейл узла: заливка. */
  dot: string;
  /** Контур точки будущей стадии. */
  outline: string;
  /** Фон полосы «Сейчас» и текущего узла. */
  surface: string;
  /** Левая акцентная полоска 4px. */
  edge: string;
  /** Текст aria-label: «Ход склада», «Ваш ход». */
  aria: string;
  /** «Что дальше» при чужом ходе: «Ждём клиента». */
  waiting: string;
  /**
   * Продолжение фразы, когда при чужом ходе кнопка всё-таки есть:
   * «Ждём клиента. Когда придёт — «Отметить оплату»». Без него блок врал
   * «от вас ничего не требуется», а кнопка рядом просила действия.
   */
  waitingAction: string;
}

/** Реестр владельцев хода. Новый владелец = запись, не ветка. */
export const ORDER_CHAIN_OWNER_META: Record<OrderChainOwner, OwnerChipMeta> = {
  MANAGER: {
    label: "Ваш ход",
    short: "вы",
    icon: UserRound,
    chip: "bg-primary text-on-primary",
    chipOutline: "bg-transparent text-primary [box-shadow:inset_0_0_0_1px_var(--primary)]",
    dot: "bg-primary",
    outline: "ring-2 ring-inset ring-primary bg-surface-lowest",
    surface: "bg-primary/8",
    edge: "bg-primary",
    aria: "Ваш ход",
    waiting: "Ваш ход",
    waitingAction: "Когда решите",
  },
  WAREHOUSE: {
    label: "Склад",
    short: "склад",
    icon: Package,
    chip: "bg-accent text-accent-foreground",
    chipOutline: "bg-transparent text-accent-foreground [box-shadow:inset_0_0_0_1px_var(--accent-foreground)]",
    dot: "bg-accent-foreground",
    outline: "ring-2 ring-inset ring-accent-foreground/40 bg-surface-lowest",
    surface: "bg-surface-low",
    edge: "bg-accent-foreground",
    aria: "Ход склада",
    waiting: "Ждём склад",
    waitingAction: "Когда закончит сборку",
  },
  CUSTOMER: {
    label: "Клиент",
    short: "клиент",
    icon: User,
    chip: "bg-warning/15 text-warning",
    chipOutline: "bg-transparent text-warning [box-shadow:inset_0_0_0_1px_var(--warning)]",
    dot: "bg-warning",
    outline: "ring-2 ring-inset ring-warning/50 bg-surface-lowest",
    surface: "bg-surface-low",
    edge: "bg-warning",
    aria: "Ход клиента",
    waiting: "Ждём клиента",
    waitingAction: "Когда придёт",
  },
  CARRIER: {
    label: "Перевозчик",
    short: "ТК",
    icon: Truck,
    chip: "bg-transparent text-on-surface-variant ring-ghost",
    chipOutline: "bg-transparent text-on-surface-variant ring-ghost",
    dot: "bg-on-surface-variant",
    outline: "ring-2 ring-inset ring-on-surface/20 bg-surface-lowest",
    surface: "bg-surface-low",
    edge: "bg-on-surface-variant",
    aria: "Ход перевозчика",
    waiting: "Ждём перевозчика",
    waitingAction: "Когда обновит статус",
  },
  SYSTEM: {
    label: "Авто",
    short: "авто",
    icon: Zap,
    chip: "bg-transparent text-on-surface-variant ring-ghost",
    chipOutline: "bg-transparent text-on-surface-variant ring-ghost",
    dot: "bg-on-surface-muted",
    outline: "ring-2 ring-inset ring-on-surface/12 bg-surface-lowest",
    surface: "bg-surface-low",
    edge: "bg-on-surface-muted",
    aria: "Переход выполнит система",
    waiting: "Ждём автоматику",
    waitingAction: "Когда сработает",
  },
};

/** Красные стикеры поверх владельца: срок и проблема (§14). */
export type OrderChainAlert = "OVERDUE" | "PROBLEM";

export const ORDER_CHAIN_ALERT_META: Record<OrderChainAlert, OwnerChipMeta> = {
  OVERDUE: {
    label: "Просрочено",
    short: "срок",
    icon: AlarmClock,
    chip: "bg-destructive text-destructive-foreground",
    chipOutline: "bg-transparent text-destructive [box-shadow:inset_0_0_0_1px_var(--destructive)]",
    dot: "bg-destructive",
    outline: "ring-2 ring-inset ring-destructive bg-surface-lowest",
    surface: "bg-destructive/8",
    edge: "bg-destructive",
    aria: "Просрочено",
    waiting: "Просрочено",
    waitingAction: "Когда решите",
  },
  PROBLEM: {
    label: "Нужно решение",
    short: "решение",
    icon: TriangleAlert,
    chip: "bg-destructive text-destructive-foreground",
    chipOutline: "bg-transparent text-destructive [box-shadow:inset_0_0_0_1px_var(--destructive)]",
    dot: "bg-destructive",
    outline: "ring-2 ring-inset ring-destructive bg-surface-lowest",
    surface: "bg-destructive/8",
    edge: "bg-destructive",
    aria: "Нужно решение",
    waiting: "Нужно решение",
    waitingAction: "Когда решите",
  },
};

/** Человеческая расшифровка проблемы — для второй строки и подсказки. */
export const ORDER_CHAIN_PROBLEM_LABEL: Record<OrderChainProblem, string> = {
  SHORTAGE: "недостача при сборке",
  AGREEMENT_OUTDATED: "заявка на доставку недействительна",
  CARRIER_SILENT: "перевозчик не обновляет статус",
  ITEM_UNLINKED: "позиция без товара каталога",
};

/** Метаданные владельца; `null` (сервер промолчал) — читается как «авто». */
export function ownerMeta(owner: OrderChainOwner | null | undefined): OwnerChipMeta {
  return ORDER_CHAIN_OWNER_META[owner ?? "SYSTEM"];
}
